import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { Section, PageHero } from "@/components/layout/Page";
import { metaTags } from "@/lib/utils/seo";
import { breadcrumbSchema, jsonLdScript } from "@/lib/utils/schema-lists";
import { StoreProductCard } from "@/components/StoreProductCard";
import { useI18n } from "@/context/I18nContext";
import { loadStoreProducts } from "@/lib/api/store-data";
import { getStoreT, useStoreT, translateStoreSsr } from "@/lib/utils/store-i18n";
import { useAccountPricing } from "@/hooks/useAccountPricing";
import { publishCanonicalOverride, publishNotFound } from "@/lib/utils/canonical-override";
import {
  canonicalSlugForId,
  categoryHreflangPaths,
  shouldRedirectToCanonicalSlug,
} from "@/lib/utils/shop-slug";
import { getCurrentLocale } from "@/lib/utils/locale";

export const Route = createFileRoute("/shop/category/$slug")({
  loader: async ({ params }) => {
    const { data: allProducts, placeholder, categories } = await loadStoreProducts();

    if (placeholder) {
      return { products: [], categoryName: params.slug, placeholder: true };
    }

    // Category slugs are translated, and Odoo — like it does for products —
    // resolves them on the trailing id alone. So another locale's slug, or a
    // misspelt one, used to fall through to notFound() and be swept to /shop by
    // resolveShopFallbackRedirect(), losing whatever link equity it carried.
    // Correct the text onto this locale's canonical slug instead, exactly as
    // shop.$slug does, and only ever when the id matches.
    const canonicalSlug = canonicalSlugForId(
      allProducts.map((p) => p.category?.slug),
      params.slug,
    );
    if (shouldRedirectToCanonicalSlug(params.slug, canonicalSlug)) {
      const lang = getCurrentLocale();
      const prefix = typeof window === "undefined" && lang !== "en" ? `/${lang}` : "";
      throw redirect({
        href: `${prefix}/shop/category/${encodeURIComponent(canonicalSlug!)}`,
        statusCode: 301,
      });
    }

    const products = allProducts.filter((p) => p.category?.slug === params.slug);
    if (products.length === 0) {
      publishNotFound(`/shop/category/${encodeURIComponent(params.slug)}`);
      throw notFound();
    }

    // VARS-124. Without this the root head() builds all ten alternates by
    // substituting the locale prefix into the requested path — so the Arabic
    // category page told Google the English URL of the category was the Arabic
    // slug with no prefix, which does not resolve. The category's real
    // per-locale URLs now come from the same listing call this loader already
    // makes (meta.categories, module 19.0.1.17.0).
    const selfPath = `/shop/category/${encodeURIComponent(params.slug)}`;
    publishCanonicalOverride({
      contentPath: selfPath,
      selfPath,
      // The `?? { [locale]: selfPath }` matters: an OMITTED perLocale means
      // "keep the prefix-substitution default", which is the defect. Against an
      // Odoo that does not send meta.categories yet, this page therefore names
      // only itself — one true alternate instead of ten, nine of them wrong.
      perLocale: categoryHreflangPaths(categories, params.slug, selfPath) ?? {
        [getCurrentLocale()]: selfPath,
      },
    });

    return {
      products,
      categoryName: products[0].category?.name ?? params.slug,
      placeholder: false,
    };
  },
  head: ({ loaderData }) => {
    // categoryName arrives from Odoo in English for every locale, so translate
    // it here too — otherwise the <title> disagrees with the visible heading.
    const raw = loaderData?.categoryName ?? "";
    const name = raw
      ? getStoreT().category(raw)
      : translateStoreSsr("store.seo.categoryFallback", "Category");
    return {
      meta: metaTags(
        `${name}${translateStoreSsr("store.seo.titleSuffix", " — VARS Store")}`,
        translateStoreSsr(
          "store.seo.categoryDescription",
          "Order {category} online from VARS Aquaculture.",
        ).replace("{category}", name),
      ),
      scripts: [
        jsonLdScript(
          "ld-breadcrumb",
          breadcrumbSchema([
            { name: translateStoreSsr("nav.home", "Home"), path: "/" },
            { name: translateStoreSsr("nav.shop", "Store"), path: "/shop" },
            { name },
          ]),
        ),
      ],
    };
  },
  component: ShopCategoryPage,
});

function ShopCategoryPage() {
  const { t } = useI18n();
  const st = useStoreT();
  const { products: ssrProducts, categoryName } = Route.useLoaderData();
  const { products } = useAccountPricing(ssrProducts);
  const localizedCategory = categoryName ? st.category(categoryName) : categoryName;

  return (
    <>
      <PageHero
        eyebrow={t("store.category.breadcrumbShop")}
        title={localizedCategory}
        variant="navy"
      >
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-mint hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("store.category.backToShop")}
        </Link>
      </PageHero>

      <Section>
        {products.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <StoreProductCard key={p.slug} product={p} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 space-y-4 glass-card rounded-3xl p-8 border border-border">
            <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto" />
            <h3 className="font-display text-xl font-bold text-navy">
              {t("store.category.notFound.title")}
            </h3>
            <p className="text-xs text-muted-foreground">{t("store.category.notFound.body")}</p>
          </div>
        )}
      </Section>
    </>
  );
}
